import { useEffect, useState } from "react";

import { IAddEmailResponse } from "@/Common/Api/AsyncActions/EmployeeAsyncActions";
import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Icon } from "@/Common/Components/IconControls/IconControls";
import { PasswordConfirmationPopup } from "@/Common/Components/Modals/PasswordConfirmationPopup";
import { MultifactorAuthenticationPopup } from "@/Common/Components/MultifactorAuthentication/MultifactorAuthenticationPopup";
import { IUseEmailUpdateStateParams } from "@/Common/Components/RequiredRecoveryEmailFlow/Hooks/useEmailUpdateState";
import { RequiredRecoveryEmailController } from "@/Common/Components/RequiredRecoveryEmailFlow/RequiredRecoveryEmailController";
import { useMount } from "@/Common/CustomHooks/useMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ISaveEmailOptions } from "@/Common/Models/Employee/ISaveEmailOptions";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { IAuthenticatorInfo } from "@/Common/Models/UI/IAuthenticatorInfo";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { IEmailAddressesModel } from "../Models/UI/IEmailAddressesModel";

interface IMultifactorAuthenticationSettingsProps {
    lastVerifiedEmail: string | null;
    currentEmail: string | null;
    AuthenticatorCodeDeliveryMethod: AuthenticatorCodeDeliveryMethod;
    emailAddressesModel: IEmailAddressesModel;

    onAuthenticatorCodeDeliveryMethodChange: (option: AuthenticatorCodeDeliveryMethod) => void;
    onSaveEmail: (options: ISaveEmailOptions, password?: string) => Promise<ClientResponse<IAddEmailResponse>>;
}

/** @deprecated Use {@link NewFlowMultifactorAuthenticationSettingsUtils} instead. */
class MultifactorAuthenticationSettingsUtils {
/**
 * Fetch the authenticator info for the current employee.
 */
    public static async fetchAuthenticatorInfo(): Promise<IAuthenticatorInfo | null> {
        const response = await Client.postAsync<IAuthenticatorInfo>(ServerOperations.Server.Myle_MultifactorAuthenticator_GetInfo, {});
        if (!response.isSuccess)
            return null;

        return response.data;
    }

    /**
 * Get whether an authenticator code is valid for the current employee.
 * @param code The code generated by the user's authenticator app.
 */
    public static tryVerifyMultifactorAuthenticatorCode(code: string): Promise<ClientResponse> {
        return Client.postAsync(
            ServerOperations.Server.Myle_MultifactorAuthenticatorCode_Verify,
            { AuthCode: code }
        );
    }

    /**
 * Toggle whether the authenticator is enabled for the current employee.
 * @param enable Whether to enable (true) or disable (false) the authenticator app.
 * @param password The required password to toggle the authenticator app.
 * @param code If enabling, the code generated by the user's authenticator app.
 */
    public static toggleAuthenticator(enable: boolean, password: string): Promise<ClientResponse> {
        return Client.postAsync(
            ServerOperations.Server.Myle_MultifactorAuthenticator_Toggle,
            {
                Enable: enable,
                Password: password
            }
        );
    }
}

/** @deprecated Use {@link NewFlowMultifactorAuthenticationSettings} instead. */
function MultifactorAuthenticationSettings(props: IMultifactorAuthenticationSettingsProps): JSX.Element {
    const { getText } = useTranslation();

    const [authenticatorInfo, setAuthenticatorInfo] = useState<IAuthenticatorInfo | null>(null);
    const [authenticatorCode, setAuthenticatorCode] = useState("");
    const [showRequiredEmailPopup, setShowRequiredEmailPopup] = useState<boolean>(!props.emailAddressesModel?.get("Emails")?.first()?.IsVerified);

    const [popupState, setPopupState] = useState({
        showConfigurationPopup: false,
        showActivationConfirmationPopup: false,
        showDeactivationConfirmationPopup: false
    });

    useEffect(
        () => setShowRequiredEmailPopup(!props.emailAddressesModel?.get("Emails")?.first()?.IsVerified),
        [props.emailAddressesModel]
    );

    useMount(async () => {
        const authenticatorInfo = await MultifactorAuthenticationSettingsUtils.fetchAuthenticatorInfo();
        setAuthenticatorInfo(authenticatorInfo);
    });

    if (!authenticatorInfo)
        return null;

    const isMultifactorAuthenticationByEmailAvailable = props.lastVerifiedEmail && cacheStore.ClinicSettings.EnableMultifactorAuthenticationByEmail;

    function closeAllPopups(reloadEmails: boolean = false): void {
        setPopupState({
            showConfigurationPopup: false,
            showActivationConfirmationPopup: false,
            showDeactivationConfirmationPopup: false
        });

        if (reloadEmails)
            sessionStore.dispatch(LocalOperations.Settings.LoadEmployeeEmails);
    }

    async function onVerifyAuthenticatorCode(): Promise<ClientResponse> {
        const response = await MultifactorAuthenticationSettingsUtils.tryVerifyMultifactorAuthenticatorCode(authenticatorCode);
        setAuthenticatorCode("");
        return response;
    }

    async function onToggleAuthenticator(enable: boolean, password: string): Promise<ClientResponse> {
        const response = await MultifactorAuthenticationSettingsUtils.toggleAuthenticator(enable, password);
        setAuthenticatorCode("");

        if (response.isSuccess) {
            setAuthenticatorInfo(s => ({ ...s, IsEnabled: enable }));
            closeAllPopups();

            let method = props.AuthenticatorCodeDeliveryMethod;
            if (!enable) {
                method = AuthenticatorCodeDeliveryMethod.NotSelected;

                // reload the authenticator info to get the new secret
                const authenticatorInfo = await MultifactorAuthenticationSettingsUtils.fetchAuthenticatorInfo();
                setAuthenticatorInfo(authenticatorInfo);
            }
            else if (method === null || method === AuthenticatorCodeDeliveryMethod.NotSelected) {
                method = AuthenticatorCodeDeliveryMethod.Application;
            }

            if (method !== props.AuthenticatorCodeDeliveryMethod)
                sessionStore.dispatch(LocalOperations.Settings.UpdateAuthenticatorCodeDeliveryMethod, { method });
        }
        return response;
    }

    function getEmailModelParams(): IUseEmailUpdateStateParams | null {
        const emailAddressModel = props.emailAddressesModel;

        if (!emailAddressModel)
            return null;

        return {
            emails: emailAddressModel.get("Emails").toArray(),
            isNotifiedByEmail: emailAddressModel.get("IsNotifiedByEmail")
        };
    }

    const showDeactivate = !cacheStore.ClinicSettings.EnableMandatoryMultifactorAuthentication
        && authenticatorInfo.IsEnabled;

    return (
        <div className="row authenticator-row">
            <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                <label className="form-label bold">{getText("MultifactorAuthenticationLabel")}</label>
            </div>
            <div className="col-sm-6 col-md-4 col-lg-4">
                {!authenticatorInfo.IsEnabled && (
                    <label className="invalid" data-cy="multifactor-authentication-settings-disabled">{getText("MultifactorAuthentication_NotConfiguredLabel")}</label>
                )}
                {authenticatorInfo.IsEnabled && (
                    isMultifactorAuthenticationByEmailAvailable
                        ? (
                            <div data-cy="multifactor-authentication-settings-enabled-email">
                                <Icon type="checkmark" color="green" />
                                <label className="margin-xs-left">{getText("MultifactorAuthentication_ActivatedLabel")}</label>
                                <div>
                                    <label
                                        onClick={() => props.onAuthenticatorCodeDeliveryMethodChange(AuthenticatorCodeDeliveryMethod.Application)}
                                    >
                                        <input
                                            type="radio"
                                            checked={props.AuthenticatorCodeDeliveryMethod === AuthenticatorCodeDeliveryMethod.Application}
                                            data-cy="multifactor-authentication-settings-application"
                                        />
                                        <span className="margin-xs-left">{getText("MultifactorAuthentication_ThirdPartyAuthenticatorAppLabel")}</span>
                                        <span className="bold"> {getText("MultifactorAuthentication_Recommended")}</span>
                                    </label>
                                </div>
                                <div>
                                    <label
                                        onClick={() => props.onAuthenticatorCodeDeliveryMethodChange(AuthenticatorCodeDeliveryMethod.Email)}
                                    >
                                        <input
                                            type="radio"
                                            checked={props.AuthenticatorCodeDeliveryMethod === AuthenticatorCodeDeliveryMethod.Email}
                                            data-cy="multifactor-authentication-settings-email"
                                        />
                                        <span className="margin-xs-left">{getText("EmailLabel")}</span>
                                    </label>
                                </div>
                            </div>
                        )
                        : (
                            <div data-cy="multifactor-authentication-settings-enabled">
                                <Icon type="checkmark" color="green" />
                                <label className="margin-xs-left">{getText("MultifactorAuthentication_ActivatedLabel")}</label>
                                <div>
                                    <span className="margin-xs-left">{getText("MultifactorAuthentication_ThirdPartyAuthenticatorAppLabel")}</span>
                                </div>
                            </div>
                        )
                )}
                {popupState.showConfigurationPopup && (
                    showRequiredEmailPopup
                        ? (
                            <RequiredRecoveryEmailController
                                isForLogin={false}
                                emailAddressesModel={getEmailModelParams()}
                                sendSaveEmailRequest={async (options, password) => {
                                    const response = await props.onSaveEmail(options, password);
                                    if (!sessionStore.isRecoveryEmailRequired && response.isSuccess && !response.data.emailAddress)
                                        closeAllPopups(true);
                                    return response;
                                }}
                                onVerifyEmailSuccess={() => setShowRequiredEmailPopup(false)}
                                onClose={() => closeAllPopups(true)}
                                showCloseButton
                            />
                        )
                        : (
                            <MultifactorAuthenticationPopup
                                authenticatorInfo={authenticatorInfo}
                                authenticatorCode={authenticatorCode}
                                lastVerifiedEmail={props.lastVerifiedEmail}
                                currentEmail={props.currentEmail}
                                onAuthenticatorCodeChange={setAuthenticatorCode}
                                onVerify={onVerifyAuthenticatorCode}
                                onSave={() => setPopupState({
                                    showConfigurationPopup: true,
                                    showActivationConfirmationPopup: true,
                                    showDeactivationConfirmationPopup: false
                                })}
                                onCancel={() => {
                                    setAuthenticatorCode("");
                                    closeAllPopups();
                                }}
                            />
                        )
                )}
                {popupState.showActivationConfirmationPopup && (
                    <PasswordConfirmationPopup
                        message={getText("MultifactorAuthentication_ConfirmActivationMessage")}
                        clearOnError
                        onConfirm={password => onToggleAuthenticator(true, password)}
                        onCancel={() => {
                            setPopupState({
                                showConfigurationPopup: true,
                                showActivationConfirmationPopup: false,
                                showDeactivationConfirmationPopup: false
                            });
                        }}
                    />
                )}
                {popupState.showDeactivationConfirmationPopup && (
                    <PasswordConfirmationPopup
                        title={getText("MultifactorAuthentication_DeactivateMultifactorAuthenticationLabel")}
                        message={getText("MultifactorAuthentication_DeactivateMultifactorAuthenticationSettingsMessage")}
                        clearOnError
                        onConfirm={password => onToggleAuthenticator(false, password)}
                        onCancel={() => closeAllPopups()}
                    />
                )}
            </div>
            <div className="col-sm-4 col-md-6 col-lg-6">
                <a
                    data-cy="multifactor-authentication-settings-modify"
                    onClick={() => setPopupState(s => ({ ...s, showConfigurationPopup: true }))}
                >
                    {getText("ModifyLabel")}
                </a>
                {showDeactivate && (
                    <>
                        <span className="padding-xs">|</span>
                        <a
                            data-cy="multifactor-authentication-settings-deactivate"
                            className="red-text"
                            onClick={() => setPopupState(s => ({ ...s, showDeactivationConfirmationPopup: true }))}
                        >
                            {getText("DeactivateLabel")}
                        </a>
                    </>
                )}
            </div>
        </div>
    );
}

export { MultifactorAuthenticationSettings, MultifactorAuthenticationSettingsUtils };
